import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { baseURL } from "common/globals";
import {
  PRACTICE_LOCALE_KEY,
  isHebrewEnvironment,
  localeFromPath,
  localeFromSearch,
  resolvePracticeLocale,
  type PracticeLocale,
} from "common/practiceLocale";

function readStoredLocale(): PracticeLocale | null {
  try {
    const stored = localStorage.getItem(PRACTICE_LOCALE_KEY);
    if (stored === "en" || stored === "he") return stored;
  } catch {
    /* private mode */
  }
  return null;
}

function persistLocale(next: PracticeLocale) {
  try {
    localStorage.setItem(PRACTICE_LOCALE_KEY, next);
  } catch {
    /* private mode */
  }
}

function browserLanguageTags(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  const list = navigator.languages;
  if (list && list.length > 0) return Array.from(list);
  return navigator.language ? [navigator.language] : [];
}

function detectHebrewEnvironment(): boolean {
  return isHebrewEnvironment({
    languages: browserLanguageTags(),
    timeZone:
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined,
  });
}

function explicitHebrewUrl(search = "", pathname = ""): boolean {
  return localeFromSearch(search) === "he" || localeFromPath(pathname) === "he";
}

function detectInitialLocale(): PracticeLocale {
  const cypress = typeof window !== "undefined" && "Cypress" in window;
  const search = typeof window !== "undefined" ? window.location.search : "";
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const next = resolvePracticeLocale({
    search,
    pathname,
    stored: readStoredLocale(),
    cypress,
  });
  // Save an explicit URL choice only. A Hebrew browser language is not
  // written, so it only unlocks the option and does not become the default.
  if (!cypress && (localeFromSearch(search) || localeFromPath(pathname))) {
    persistLocale(next);
  }
  return next;
}

/** True for `vite` dev even if a local `.env` sets NODE_ENV=production. */
function isDevRuntime(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "development";
}

export const usePracticeLocaleStore = defineStore("practiceLocale", () => {
  const locale = ref<PracticeLocale>(detectInitialLocale());
  /** Vite dev, Hebrew/Israeli client env, a Hebrew URL, or a production visit from Israel. */
  const optionAvailable = ref(
    isDevRuntime() ||
      detectHebrewEnvironment() ||
      (typeof window !== "undefined" &&
        explicitHebrewUrl(window.location.search, window.location.pathname)),
  );
  const tutorLocale = computed<PracticeLocale>(() =>
    optionAvailable.value && locale.value === "he" ? "he" : "en",
  );

  let refreshStarted = false;

  function setLocale(next: PracticeLocale, opts?: { force?: boolean }) {
    const changed = locale.value !== next;
    locale.value = next;
    // The toggle and a URL param are explicit choices and are saved.
    // `force` also saves when the URL repeats the current locale.
    // A re-emitted toggle value is not saved. The UI and tutor follow optionAvailable.
    if (changed || opts?.force) persistLocale(next);
  }

  async function refreshHebrewOption() {
    if (
      isDevRuntime() ||
      detectHebrewEnvironment() ||
      (typeof window !== "undefined" &&
        explicitHebrewUrl(window.location.search, window.location.pathname))
    ) {
      optionAvailable.value = true;
      return;
    }
    if (refreshStarted) return;
    refreshStarted = true;
    try {
      const response = await fetch(`${baseURL}/practice-hebrew-available`);
      if (!response.ok) {
        optionAvailable.value = false;
        return;
      }
      const data = (await response.json()) as { available?: boolean };
      optionAvailable.value = data.available === true;
    } catch {
      optionAvailable.value = false;
    }
  }

  return {
    locale,
    optionAvailable,
    tutorLocale,
    setLocale,
    refreshHebrewOption,
  };
});
